import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PokemonConfig {
  id: string;
  name: string;
  pokedex_number: number | null;
  sprite_url: string | null;
  initial_votes: number;
  is_active: boolean;
  created_at: string;
  display_order: number | null;
}



export function usePokemonConfig() {
  return useQuery({
    queryKey: ["pokemon_config"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pokemon_config")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true, nullsFirst: false })
        .order("name");
      if (error) throw error;
      return data as PokemonConfig[];
    },
  });
}

export function useAllPokemonConfig() {
  return useQuery({
    queryKey: ["pokemon_config_all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pokemon_config")
        .select("*")
        .order("display_order", { ascending: true, nullsFirst: false })
        .order("name");
      if (error) throw error;
      return data as PokemonConfig[];
    },
  });
}

export function useAddPokemon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { name: string; pokedex_number?: number; sprite_url?: string; initial_votes?: number }) => {
      // Compute next display_order
      const { data: maxRow } = await supabase
        .from("pokemon_config")
        .select("display_order")
        .order("display_order", { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();
      const nextOrder = (maxRow?.display_order ?? 0) + 1;

      const { error } = await supabase.from("pokemon_config").insert({
        name: p.name,
        pokedex_number: p.pokedex_number ?? null,
        sprite_url: p.sprite_url ?? null,
        initial_votes: p.initial_votes ?? 0,
        display_order: nextOrder,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pokemon_config"] });
      qc.invalidateQueries({ queryKey: ["pokemon_config_all"] });
    },
  });
}

export function useUpdatePokemon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: {
      id: string;
      name?: string;
      pokedex_number?: number | null;
      sprite_url?: string | null;
      initial_votes?: number;
    }) => {
      const update: {
        name?: string;
        pokedex_number?: number | null;
        sprite_url?: string | null;
        initial_votes?: number;
      } = {};
      if (p.name !== undefined) update.name = p.name;
      if (p.pokedex_number !== undefined) update.pokedex_number = p.pokedex_number;
      if (p.sprite_url !== undefined) update.sprite_url = p.sprite_url;
      if (p.initial_votes !== undefined) update.initial_votes = p.initial_votes;
      const { error } = await supabase.from("pokemon_config").update(update as never).eq("id", p.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pokemon_config"] });
      qc.invalidateQueries({ queryKey: ["pokemon_config_all"] });
    },
  });
}

export function useDeletePokemon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pokemon_config").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pokemon_config"] });
      qc.invalidateQueries({ queryKey: ["pokemon_config_all"] });
    },
  });
}

export function useReorderPokemon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      // Persist new display_order = index + 1
      await Promise.all(
        orderedIds.map((id, index) =>
          supabase.from("pokemon_config").update({ display_order: index + 1 }).eq("id", id)
        )
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pokemon_config"] });
      qc.invalidateQueries({ queryKey: ["pokemon_config_all"] });
    },
  });
}

export async function uploadPokemonImage(file: File): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase() || "png";
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from("pokemon-images").upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("pokemon-images").getPublicUrl(path);
  return data.publicUrl;
}
